import axios from "axios"
import store from "@/store"
import router from "@/routes"

const instance = axios.create({
  withCredentials: true, // 쿠키 전송 허용
})

/**
 * Request 인터셉터
 */
instance.interceptors.request.use(
  (config) => {
    // Access Token 추가
    const token = store.state.accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/**
 * Response 인터셉터
 */
instance.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    // 카테고리 운영 테스트 화면은 본사이트 로그인을 건드리지 않는다(로그아웃·로그인 화면 이동 안 함).
    if (process.env.VUE_APP_PREMIUM_TEST === "true") return Promise.reject(error)
    const originalRequest = error.config

    // 옛 로그인이 만료돼도 공개 화면(신작 목록·컨셉·예약 고르기)은 그대로 둔다. 로그인 화면은 로그인이 꼭 필요한 화면에서만 보낸다.
    // (예전에 로그인했던 손님이 인스타 디엠 링크로 와도 목록 대신 로그인 화면이 뜨던 문제, 10/4)
    const redirectToLogin = () => {
      const currentRoute = router.currentRoute || {}
      const currentPath = currentRoute.path || ""
      const isAdminContext = currentPath.startsWith("/admin")
      const needsLogin = isAdminContext || (currentRoute.matched || []).some((record) => record.meta && record.meta.requiresAuth)
      if (!needsLogin) return

      const targetPath = isAdminContext ? "/admin/auth/login" : "/auth/login"
      const returnPath = currentRoute.fullPath || currentPath || "/"

      if (currentPath !== targetPath) {
        router.push({
          path: targetPath,
          // 손님 로그인 화면은 callbackUrl, 관리자 로그인 화면은 redirect를 읽는다
          query: isAdminContext ? { redirect: returnPath } : { callbackUrl: returnPath },
        })
      }
    }

    // 토큰 만료 에러 처리 (TOKEN_EXPIRED)
    if (error.response?.status === 401 && error.response?.data?.code === "TOKEN_EXPIRED" && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        console.log("🔄 토큰 만료 감지 - Refresh Token으로 갱신 시도")

        // 토큰 갱신
        await store.dispatch("refreshAccessToken")

        // 새로운 토큰으로 원래 요청 재시도
        const newToken = store.state.accessToken
        originalRequest.headers.Authorization = `Bearer ${newToken}`

        console.log("✅ 토큰 갱신 성공 - 원래 요청 재시도")
        return instance(originalRequest)
      } catch (refreshError) {
        // 갱신 실패 시 로그인 페이지로 이동
        console.error("❌ 토큰 갱신 실패 - 로그인 페이지로 이동")
        store.dispatch("logout")

        redirectToLogin()

        return Promise.reject(refreshError)
      }
    }

    // 잘못된 토큰 에러 처리 (INVALID_TOKEN)
    if (error.response?.status === 403 && error.response?.data?.code === "INVALID_TOKEN") {
      console.error("❌ 유효하지 않은 토큰 - 로그아웃 처리")
      store.dispatch("logout")

      redirectToLogin()
    }

    // 그 외 401 에러는 로그인 페이지로
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.warn("⚠️ 인증 필요 - 로그인 페이지로 이동")
      store.dispatch("logout")

      redirectToLogin()
    }

    return Promise.reject(error)
  }
)

export default instance
